import PropTypes from 'prop-types';
import { Grid } from '@material-ui/core';
import { ReadOnlyFormControl } from 'components/common/fields';
import { makeStyles } from '@material-ui/core/styles';
import { dateAndTime } from 'util/dateAndTime';

const NOT_AVAILABLE = 'N/A';
const STATUS_UPDATED_AT_FORMAT = /^\d{2}-\d{2}-\d{4}, \d{1,2}:\d{2} [AP]M ET$/;

const formatCardType = (cardType) => {
  const normalized = String(cardType || '').toLowerCase();

  if (normalized === 'dc') return 'Debit';
  if (normalized === 'cc') return 'Credit';
  return cardType || NOT_AVAILABLE;
};

const formatMaskedCardNumber = (cardNumberMask) =>
  cardNumberMask ? `\u2022\u2022\u2022\u2022 ${cardNumberMask}` : NOT_AVAILABLE;

const formatStatus = (status) => {
  if (!status) return NOT_AVAILABLE;
  const normalized = String(status).toLowerCase();
  return `${normalized.charAt(0).toUpperCase()}${normalized.slice(1)}`;
};

const formatExpiration = (month, year) => {
  const normalizedMonth =
    month == null || month === '' ? '' : String(month).padStart(2, '0');
  const normalizedYear = year == null || year === '' ? '' : String(year);

  if (!normalizedMonth || !normalizedYear) {
    return NOT_AVAILABLE;
  }

  return `${normalizedMonth}/${normalizedYear}`;
};

const capitalizeFirstLetter = (value) => {
  if (typeof value !== 'string' || !value.trim()) {
    return value;
  }

  return `${value.charAt(0).toUpperCase()}${value.slice(1)}`;
};

const formatCardholderName = (firstName, lastName) => {
  const parts = [firstName, lastName]
    .filter(Boolean)
    .map((part) => capitalizeFirstLetter(String(part).trim()));

  return parts.length ? parts.join(' ') : NOT_AVAILABLE;
};

const formatStatusLastUpdated = (value) => {
  if (typeof value !== 'string' || !value.trim()) {
    return value || NOT_AVAILABLE;
  }

  if (value === NOT_AVAILABLE || STATUS_UPDATED_AT_FORMAT.test(value)) {
    return value;
  }

  const formatted = dateAndTime(value);
  return formatted === 'Invalid date' ? value : formatted;
};

const formatStatusUpdatedBy = (item) =>
  item.updatedBy ||
  item.updatedByEmail ||
  item.updatedUserEmail ||
  item.updatedUser ||
  item.updatedAdminUserId ||
  NOT_AVAILABLE;

const useStyles = makeStyles((theme) => ({
  grid: {
    marginBottom: theme.spacing(2),
    marginTop: theme.spacing(1),
  },
}));

const DetailsRender = ({ item }) => {
  const classes = useStyles();
  const address = item.uspsAddress || {};
  const cardHolderName = formatCardholderName(
    item.cardHolderFirstName,
    item.cardHolderLastName
  );
  const expiration = formatExpiration(
    item.cardExpireMonth,
    item.cardExpireYear
  );
  const statusLastUpdated =
    item.updatedAtLabel ||
    item.statusAtLabel ||
    item.updatedAt ||
    NOT_AVAILABLE;
  const billingStreet2 = address.street2 || NOT_AVAILABLE;
  const billingStateZip =
    address.state && address.zip
      ? `${address.state} ${address.zip}`
      : address.state || address.zip || NOT_AVAILABLE;

  return (
    <Grid className={classes.grid} container>
      <Grid item xs={4}>
        <ReadOnlyFormControl
          label="Type"
          value={formatCardType(item.cardType)}
        />
      </Grid>
      <Grid item xs={4}>
        <ReadOnlyFormControl
          label="Nickname"
          value={item.nickName || NOT_AVAILABLE}
        />
      </Grid>
      <Grid item xs={4}>
        <ReadOnlyFormControl label="Cardholder Name" value={cardHolderName} />
      </Grid>
      <Grid item xs={4}>
        <ReadOnlyFormControl
          label="Card Number"
          value={formatMaskedCardNumber(item.cardNumberMask || item.cardNumber)}
        />
      </Grid>
      <Grid item xs={4}>
        <ReadOnlyFormControl
          label="Status"
          value={formatStatus(item.cardStatus)}
        />
      </Grid>
      <Grid item xs={4}>
        <ReadOnlyFormControl label="Expiration" value={expiration} />
      </Grid>
      <Grid item xs={4}>
        <ReadOnlyFormControl
          label="Status Last Updated"
          value={formatStatusLastUpdated(statusLastUpdated)}
        />
      </Grid>
      <Grid item xs={4}>
        <ReadOnlyFormControl
          label="Status Updated By"
          value={formatStatusUpdatedBy(item)}
        />
      </Grid>
      <Grid item xs={4}>
        <ReadOnlyFormControl
          label="Billing Street 1"
          value={address.street1 || NOT_AVAILABLE}
        />
      </Grid>
      <Grid item xs={4}>
        <ReadOnlyFormControl label="Billing Street 2" value={billingStreet2} />
      </Grid>
      <Grid item xs={4}>
        <ReadOnlyFormControl
          label="Billing City"
          value={capitalizeFirstLetter(address.city || NOT_AVAILABLE)}
        />
      </Grid>
      <Grid item xs={4}>
        <ReadOnlyFormControl
          label="Billing State / ZIP"
          value={billingStateZip}
        />
      </Grid>
    </Grid>
  );
};

DetailsRender.propTypes = {
  item: PropTypes.shape({
    nickName: PropTypes.string,
    cardType: PropTypes.string,
    cardHolderFirstName: PropTypes.string,
    cardHolderLastName: PropTypes.string,
    cardNumberMask: PropTypes.string,
    cardNumber: PropTypes.string,
    cardStatus: PropTypes.string,
    cardExpireMonth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    cardExpireYear: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    updatedAt: PropTypes.string,
    updatedAtLabel: PropTypes.string,
    statusAtLabel: PropTypes.string,
    updatedBy: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    updatedByEmail: PropTypes.string,
    updatedUser: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    updatedUserEmail: PropTypes.string,
    updatedAdminUserId: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
    ]),
    uspsAddress: PropTypes.shape({
      street1: PropTypes.string,
      street2: PropTypes.string,
      city: PropTypes.string,
      state: PropTypes.string,
      zip: PropTypes.string,
    }),
  }).isRequired,
};

export default DetailsRender;
